import { motion } from "framer-motion";
import { ArrowDown, Sparkles, Zap, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";
import Logo3D from "./Logo3D";
import Scene3D from "./Scene3D";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.9, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] },
    }),
};

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
    const { t } = useI18n();
    return (
        <section id="hero" className="relative overflow-hidden pb-16 pt-32 sm:pt-36 lg:min-h-screen lg:pb-0 lg:pt-24" data-testid="hero">
            <div className="grid-lines absolute inset-0" />
            <div className="absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-brand-violet/20 blur-[140px]" />
            <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-brand-coral/15 blur-[140px]" />
            <Scene3D />

            <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5"
                        data-testid="hero-badge"
                    >
                        <span className="h-2 w-2 rounded-full bg-gradient-to-r from-brand-coral to-brand-blue" />
                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-300">
                            {t.badge}
                        </span>
                    </motion.div>

                    <h1 className="font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                        <span className="block overflow-hidden pb-1">
                            <motion.span key={t.h1a} custom={0} variants={line} initial="hidden" animate="show" className="block">
                                {t.h1a}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span key={t.h1b} custom={1} variants={line} initial="hidden" animate="show" className="block">
                                {t.h1b}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span key={t.h1c1} custom={2} variants={line} initial="hidden" animate="show" className="block">
                                {t.h1c1} <span className="text-gradient">{t.h1c2}</span>
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="mt-6 max-w-md text-base leading-relaxed text-zinc-400 sm:text-lg"
                        data-testid="hero-subline"
                    >
                        {t.sub}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.52 }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollTo("kalkulator")}
                            data-testid="hero-cta"
                            className="rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue px-7 py-3.5 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.04] active:scale-95"
                        >
                            {t.cta1}
                        </button>
                        <button
                            onClick={() => scrollTo("cara-kerja")}
                            data-testid="hero-cta-secondary"
                            className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-zinc-200 transition-colors hover:border-white/40"
                        >
                            {t.cta2}
                            <ArrowDown size={15} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-zinc-500"
                    >
                        <span className="flex items-center gap-1.5" data-testid="hero-chip-gratis">
                            <Sparkles size={13} className="text-brand-violet" /> {t.c1}
                        </span>
                        <span className="flex items-center gap-1.5" data-testid="hero-chip-instan">
                            <Zap size={13} className="text-brand-coral" /> {t.c2}
                        </span>
                        <span className="flex items-center gap-1.5" data-testid="hero-chip-privat">
                            <ShieldCheck size={13} className="text-brand-blue" /> {t.c3}
                        </span>
                    </motion.div>
                </div>

                <Logo3D />
            </div>
        </section>
    );
}
